/**
 * Luxor — 3D Pharaoh's Grid: surface geometry.
 *
 * The board is a regular tetrahedron. The three side faces (A, B, C) are
 * playable; the base is not. Each face is an edge-length-ROWS triangle
 * subdivided into unit triangles, ROWS rows tall.
 *
 * ---------------------------------------------------------------------------
 * Cell coordinates
 * ---------------------------------------------------------------------------
 * Within a face every cell carries a triple (a, b, c) of non-negative ints
 * giving its distance from each of the face's three edges:
 *
 *   a + b + c === ROWS - 1  ->  upward-pointing triangle
 *   a + b + c === ROWS - 2  ->  downward-pointing triangle
 *
 * Each coordinate names one of the three primary alignment axes of the design
 * doc, and holding one of them constant traces a straight row:
 *
 *   a  ->  120 deg axis, parallel to the face's RIGHT edge   (a === const)
 *   b  ->   60 deg axis, parallel to the face's LEFT edge    (b === const)
 *   c  ->    0 deg axis, parallel to the face's BASE         (c === const)
 *
 * A cell's three edges carry the same labels: crossing the `a` edge changes
 * `a`, and so on. Upward cells reach a neighbour by DECREMENTING a coordinate,
 * downward cells by INCREMENTING one — so downward cells are always interior
 * and only upward cells sit on a face boundary.
 *
 * The three faces are independent boards. Neither pieces nor rows cross a
 * seam, so `step` simply reports no neighbour at a face edge.
 */

/** Rows per face. Everything else scales from this. */
export const ROWS = 4;

export const FACE_COUNT = 3;
export const CELLS_PER_FACE = ROWS * ROWS;
export const CELL_COUNT = FACE_COUNT * CELLS_PER_FACE;

/** Axis indices. */
export const AXIS_A = 0; // 120 deg  (parallel to the face's right edge)
export const AXIS_B = 1; //  60 deg  (parallel to the face's left edge)
export const AXIS_C = 2; //   0 deg  (parallel to the face's base)

export const AXES = [AXIS_A, AXIS_B, AXIS_C];
export const AXIS_NAMES = ['120°', '60°', '0°'];

const UP_SUM = ROWS - 1;
const DOWN_SUM = ROWS - 2;

/** First flattened column index of each row: 0, 1, 4, 9, ... */
const ROW_OFFSET = Array.from({ length: ROWS }, (_, r) => r * r);

/** Stable integer id for a cell. */
export function cellId(face, row, col) {
  return face * CELLS_PER_FACE + ROW_OFFSET[row] + col;
}

/** (row, col) -> (a, b, c). Even columns point up, odd columns point down. */
export function rowColToAbc(row, col) {
  if (col % 2 === 0) {
    const k = col / 2; // index among the row's upward cells
    return [row - k, k, ROWS - 1 - row];
  }
  const j = (col - 1) / 2; // index among the row's downward cells
  return [row - 1 - j, j, ROWS - 1 - row];
}

/** (a, b, c) -> (row, col), or null when the triple is off-face. */
export function abcToRowCol(a, b, c) {
  if (a < 0 || b < 0 || c < 0) return null;
  const sum = a + b + c;
  if (sum !== UP_SUM && sum !== DOWN_SUM) return null;
  const row = ROWS - 1 - c;
  if (row < 0 || row >= ROWS) return null;
  const col = sum === UP_SUM ? 2 * b : 2 * b + 1;
  if (col > 2 * row) return null;
  return [row, col];
}

/**
 * The cells, indexed by id.
 * Each entry: { id, face, row, col, a, b, c, up }
 */
export const CELLS = [];

for (let face = 0; face < FACE_COUNT; face += 1) {
  for (let row = 0; row < ROWS; row += 1) {
    for (let col = 0; col <= 2 * row; col += 1) {
      const [a, b, c] = rowColToAbc(row, col);
      CELLS[cellId(face, row, col)] = {
        id: cellId(face, row, col),
        face,
        row,
        col,
        a,
        b,
        c,
        up: col % 2 === 0,
      };
    }
  }
}

/**
 * Step from a cell across one of its three edges.
 *
 * @param {number} id   cell to leave
 * @param {number} axis which of the cell's three edges to cross
 * @returns {number | null} the neighbour's id, or null at a face edge.
 */
export function step(id, axis) {
  const cell = CELLS[id];
  const abc = [cell.a, cell.b, cell.c];

  // Upward cells decrement, downward cells increment.
  abc[axis] += cell.up ? -1 : 1;

  const rc = abcToRowCol(abc[0], abc[1], abc[2]);
  return rc ? cellId(cell.face, rc[0], rc[1]) : null;
}

/**
 * The straight rows of one face, along all three axes.
 *
 * Holding a coordinate at `k` selects the row that many steps in from the
 * matching edge; it holds 2 * (ROWS - 1 - k) + 1 cells, so k runs from the
 * face's longest row (its base, or a full-length diagonal) down to a single
 * corner cell. Rows shorter than `minLength` are dropped.
 *
 * @returns {{face: number, axis: number, index: number, cells: number[]}[]}
 */
export function faceRows(minLength = 3) {
  const rows = [];

  for (let face = 0; face < FACE_COUNT; face += 1) {
    for (const axis of AXES) {
      for (let k = 0; k < ROWS; k += 1) {
        const cells = CELLS.filter(
          (cell) => cell.face === face && [cell.a, cell.b, cell.c][axis] === k,
        ).map((cell) => cell.id);
        if (cells.length >= minLength) rows.push({ face, axis, index: k, cells });
      }
    }
  }

  return rows;
}

/** Every non-negative (A, B, C) summing to n. */
function triples(n) {
  const out = [];
  for (let a = 0; a <= n; a += 1) {
    for (let b = 0; b <= n - a; b += 1) out.push([a, b, n - a - b]);
  }
  return out;
}

/**
 * The sub-triangles of side `k` on each face — the larger triangles a set of
 * k * k cells can form.
 *
 * In these coordinates an upward sub-triangle is simply a corner cut: the cells
 * at least A, B and C in from the three edges, where A + B + C = ROWS - k. A
 * downward sub-triangle is the mirror of that, capped rather than floored.
 * Candidates that the face edge clips short of k * k cells are dropped, which
 * is how downward triangles thin out — a 4-row face holds only one per face,
 * and no downward triangle of side 3 at all.
 *
 * @returns {{face: number, side: number, pointsUp: boolean, cells: number[]}[]}
 */
export function faceTriangles(k) {
  const out = [];
  const seen = new Set();

  const add = (face, pointsUp, cells) => {
    if (cells.length !== k * k) return;
    const key = `${pointsUp}:${cells.join(',')}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ face, side: k, pointsUp, cells });
  };

  for (let face = 0; face < FACE_COUNT; face += 1) {
    const cells = CELLS.filter((cell) => cell.face === face);

    for (const [A, B, C] of triples(ROWS - k)) {
      add(
        face,
        true,
        cells.filter((c) => c.a >= A && c.b >= B && c.c >= C).map((c) => c.id),
      );
    }

    const capSum = ROWS - 3 + k;
    if (capSum >= 0) {
      for (const [A, B, C] of triples(capSum)) {
        add(
          face,
          false,
          cells.filter((c) => c.a <= A && c.b <= B && c.c <= C).map((c) => c.id),
        );
      }
    }
  }

  return out;
}

/* ---------------------------------------------------------------------------
 * 3D placement
 * ------------------------------------------------------------------------- */

const SIDE = ROWS; // edge length in cells, so every cell has edge 1
const BASE_HALF = SIDE / 2;
const BASE_DEPTH = (SIDE * Math.sqrt(3)) / 2;
const APEX_HEIGHT = SIDE * Math.sqrt(2 / 3);

/** Centre the tetrahedron on its centroid so orbiting looks balanced. */
const Y_OFFSET = -APEX_HEIGHT / 4;

export const APEX = [0, APEX_HEIGHT + Y_OFFSET, 0];

/** Base corners, ordered so face f === (APEX, BASE_VERTS[f], BASE_VERTS[f + 1]). */
export const BASE_VERTS = [
  [-BASE_HALF, Y_OFFSET, BASE_DEPTH / 3],
  [BASE_HALF, Y_OFFSET, BASE_DEPTH / 3],
  [0, Y_OFFSET, -(2 * BASE_DEPTH) / 3],
];

/** Camera azimuth (radians) that squares the camera up with a face. */
export function faceAzimuth(face) {
  return (face * 2 * Math.PI) / FACE_COUNT;
}

export const FACE_LABELS = ['A', 'B', 'C'];

function lerp3(from, to, t) {
  return [
    from[0] + (to[0] - from[0]) * t,
    from[1] + (to[1] - from[1]) * t,
    from[2] + (to[2] - from[2]) * t,
  ];
}

/**
 * Lattice vertex (i, j) of a face: i rows down from the apex, j steps along
 * that row from its left end. 0 <= j <= i <= ROWS.
 */
function faceVertex(face, i, j) {
  const left = BASE_VERTS[face];
  const right = BASE_VERTS[(face + 1) % FACE_COUNT];
  const rowLeft = lerp3(APEX, left, i / ROWS);
  const rowRight = lerp3(APEX, right, i / ROWS);
  return i === 0 ? rowLeft : lerp3(rowLeft, rowRight, j / i);
}

/** The three 3D corners of a cell, wound counter-clockwise seen from outside. */
export function cellCorners(id) {
  const { face, row, col, up } = CELLS[id];
  const tri = up
    ? [
        faceVertex(face, row, col / 2),
        faceVertex(face, row + 1, col / 2),
        faceVertex(face, row + 1, col / 2 + 1),
      ]
    : [
        faceVertex(face, row, (col - 1) / 2),
        faceVertex(face, row, (col - 1) / 2 + 1),
        faceVertex(face, row + 1, (col - 1) / 2 + 1),
      ];

  // Flip winding if the normal ended up pointing into the pyramid.
  const [p, q, r] = tri;
  const u = [q[0] - p[0], q[1] - p[1], q[2] - p[2]];
  const v = [r[0] - p[0], r[1] - p[1], r[2] - p[2]];
  const n = [
    u[1] * v[2] - u[2] * v[1],
    u[2] * v[0] - u[0] * v[2],
    u[0] * v[1] - u[1] * v[0],
  ];
  const mid = [
    (p[0] + q[0] + r[0]) / 3,
    (p[1] + q[1] + r[1]) / 3 - Y_OFFSET,
    (p[2] + q[2] + r[2]) / 3,
  ];
  const outward = n[0] * mid[0] + n[1] * mid[1] + n[2] * mid[2];
  return outward < 0 ? [p, r, q] : tri;
}

const SQRT3_2 = Math.sqrt(3) / 2;

/**
 * An orthonormal frame for laying flat 2D artwork onto a face.
 *
 * A face's lattice vertex (i, j) is affine in i and j, so a point (x, y) in the
 * same 2D lattice the tray draws in — x along a row, y down the face — lands at
 * `vertex(i0, j0) + x * right + y * down`. The two directions come out unit
 * length and perpendicular, so (right, down, right x down) is a true rotation
 * and a piece can be positioned with one transform rather than per-cell.
 *
 * @returns {{right: number[], down: number[], normal: number[]}}
 */
export function faceBasis(face) {
  const left = BASE_VERTS[face];
  const right = BASE_VERTS[(face + 1) % FACE_COUNT];
  const e1 = left.map((v, i) => (v - APEX[i]) / ROWS); // apex -> bottom-left
  const e2 = right.map((v, i) => (v - left[i]) / ROWS); // along a row

  return {
    right: e2,
    down: e1.map((v, i) => (v + e2[i] / 2) / SQRT3_2),
    normal: faceNormal(face),
  };
}

/** World position of a face's lattice vertex (i rows down, j along that row). */
export function latticeVertex(face, i, j) {
  const left = BASE_VERTS[face];
  const right = BASE_VERTS[(face + 1) % FACE_COUNT];
  return APEX.map(
    (v, axis) =>
      v + (i * (left[axis] - APEX[axis])) / ROWS + (j * (right[axis] - left[axis])) / ROWS,
  );
}

/** The lattice vertex a cell's own 2D drawing hangs from. */
export function cellLatticeOrigin(id) {
  const cell = CELLS[id];
  return { i: ROWS - 1 - cell.c, j: cell.b };
}

/** Cell centroid in world space. */
export function cellCentroid(id) {
  const [p, q, r] = cellCorners(id);
  return [(p[0] + q[0] + r[0]) / 3, (p[1] + q[1] + r[1]) / 3, (p[2] + q[2] + r[2]) / 3];
}

/**
 * The outline of a set of cells, edge by edge.
 *
 * Which edges are on the border is a counting argument: two cells that touch
 * meet along exactly one edge, so an edge seen twice is interior and an edge
 * seen once is on the border. Corners are matched on rounded coordinates
 * because the two cells sharing an edge compute its endpoints by different
 * routes through `faceVertex` and land a float's breadth apart.
 *
 * Each border edge is reported with a frame, which is what lets something be
 * *aimed along* it rather than merely drawn on it: `along` runs down the edge,
 * `outward` lies in the face and points away from the shape, and the face
 * normal completes the set. All three are unit length and mutually
 * perpendicular.
 *
 * Every cell is a unit triangle, so every edge is exactly 1 board unit long —
 * an emitter sized once fits them all.
 *
 * @param {number[]} cells ids, all on one face
 * @returns {{a: number[], b: number[], mid: number[], along: number[],
 *            outward: number[], normal: number[]}[]}
 */
export function outlineEdges(cells) {
  if (!cells?.length) return [];

  const key = (p) => p.map((v) => v.toFixed(4)).join(',');
  const edges = new Map();
  for (const id of cells) {
    const corners = cellCorners(id);
    for (let i = 0; i < 3; i += 1) {
      const a = corners[i];
      const b = corners[(i + 1) % 3];
      const k = [key(a), key(b)].sort().join('|');
      const seen = edges.get(k);
      if (seen) seen.count += 1;
      else edges.set(k, { count: 1, a, b });
    }
  }

  const normal = faceNormal(CELLS[cells[0]].face);

  // The middle of the shape, so an edge's two in-plane perpendiculars can be
  // told apart: the one pointing away from here is the outward one.
  const centre = [0, 0, 0];
  for (const id of cells) {
    const c = cellCentroid(id);
    for (let k = 0; k < 3; k += 1) centre[k] += c[k] / cells.length;
  }

  const out = [];
  for (const { count, a, b } of edges.values()) {
    if (count !== 1) continue;

    const d = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
    const len = Math.hypot(d[0], d[1], d[2]) || 1;
    const along = [d[0] / len, d[1] / len, d[2] / len];
    const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];

    // normal x along is the in-plane perpendicular; its sign is arbitrary, so
    // flip it to the side facing away from the shape.
    let outward = [
      normal[1] * along[2] - normal[2] * along[1],
      normal[2] * along[0] - normal[0] * along[2],
      normal[0] * along[1] - normal[1] * along[0],
    ];
    const away =
      outward[0] * (mid[0] - centre[0]) +
      outward[1] * (mid[1] - centre[1]) +
      outward[2] * (mid[2] - centre[2]);
    if (away < 0) outward = [-outward[0], -outward[1], -outward[2]];

    out.push({ a, b, mid, along, outward, normal });
  }
  return out;
}

/** Outward unit normal of the face a cell belongs to. */
export function faceNormal(face) {
  const [p, q, r] = [APEX, BASE_VERTS[face], BASE_VERTS[(face + 1) % FACE_COUNT]];
  const u = [q[0] - p[0], q[1] - p[1], q[2] - p[2]];
  const v = [r[0] - p[0], r[1] - p[1], r[2] - p[2]];
  let n = [
    u[1] * v[2] - u[2] * v[1],
    u[2] * v[0] - u[0] * v[2],
    u[0] * v[1] - u[1] * v[0],
  ];
  const len = Math.hypot(n[0], n[1], n[2]) || 1;
  n = [n[0] / len, n[1] / len, n[2] / len];
  // Point away from the axis of the pyramid.
  const mid = [(p[0] + q[0] + r[0]) / 3, 0, (p[2] + q[2] + r[2]) / 3];
  if (n[0] * mid[0] + n[2] * mid[2] < 0) n = [-n[0], -n[1], -n[2]];
  return n;
}
